import { BxfDefaultState } from '@blk/bxf-web-components';
import { useEffect, useState } from 'react';
import packageInfo from '../../package.json';
import { CHAT_BLK, CHAT_BLK_BETA, DEFAULT, URIS } from '../constants';
import { ChatBlkBaseService, ChatBlkServiceFactory } from '../services';
import {
    ChatBlkAppContext,
    CopilotChatInitialWidth,
    PromptRequestAiEngineEnum,
} from '../types';
import { getWindow, resetChatImageStorage, updateWindowTitle } from '../utils';
/**
 * Custom hook to initialize the ChatBlk service.
 * - Sets the `isChatEnabled` flag to true in the `BxfDefaultState`.
 * - Initializes the chat service and updates the window title if the chat is not embedded.
 * - Resets the chat image storage on component unmount.
 * - Initializes the chat service based on the app context or deep link state.
 * - If no app context or deep link state is found, it initializes a default service.
 *
 * @param {ChatBlkBaseService} [service] - Optional instance of ChatBlkBaseService.
 * @returns {Object} - An object containing the `isEmbedded` state and the `serviceInstance`.
 *
 * @property {boolean} isEmbedded - State indicating if the chat is embedded.
 * @property {ChatBlkBaseService | undefined} serviceInstance - Instance of the ChatBlkBaseService.
 *
 * @example
 * const { isEmbedded, serviceInstance } = useInitServices();
 */
const useInitServices = (
    service?: ChatBlkBaseService,
    appContext?: ChatBlkAppContext
): { isEmbedded: boolean; serviceInstance: ChatBlkBaseService } => {
    const isEmbedded = true;
    const [serviceInstance, setServiceInstance] = useState(service);
    BxfDefaultState.isChatEnabled = true;
    useEffect(() => {
        !serviceInstance && init();
        return () => {
            resetChatImageStorage(ChatBlkBaseService.getInstance()?.appContext);
        };
    }, []);

    /**
     * Update the window title with the app name
     */
    useEffect(() => {
        ChatBlkBaseService.getInstance().displayAppName !== document.title &&
            !isEmbedded &&
            updateWindowTitle(ChatBlkBaseService.getInstance().displayAppName);
    }, [isEmbedded]);

    const init = () => {
        if (ChatBlkBaseService.getInstance()?.appContext) {
            console.log(
                `[${CHAT_BLK}] Found app context in ChatService:`,
                ChatBlkBaseService.getInstance().appContext
            );
            return true;
        } else if (appContext && appContext.aiProps !== undefined) {
            const newService = ChatBlkServiceFactory.initServices(
                appContext,
                true
            );
            console.log(
                `[${CHAT_BLK}] Initialized ChatService with app context:`,
                newService.baseInstance.appContext
            );
        } else {
            initializeDefaultService();
        }

        setServiceInstance(ChatBlkBaseService.getInstance());
        resetChatImageStorage(ChatBlkBaseService.getInstance()?.appContext);
    };

    const initializeDefaultService = () => {
        const href = getWindow()?.location?.href;
        const appName = href.includes('chatblk-beta')
            ? CHAT_BLK_BETA
            : CHAT_BLK;
        const appVersion = packageInfo.version;
        const displayAppName = 'BlackRock.com';
        const aiEngine = PromptRequestAiEngineEnum.ChatBlkV1;

        const defaultAppContext: ChatBlkAppContext = {
            hasHeader: true,
            displayMessage: {
                primary: `Hi, I'm ${appName}, your AI-powered assistant. I can help answer questions about how to use ${displayAppName}.`,
                displayAppName,
            },
            initialWidth: CopilotChatInitialWidth.MEDIUM,
            aiProps: {
                appName,
                appVersion,
                aiEngine,
            },
            isProd: URIS.PROD_HOSTS.includes(window.location.hostname),
            userInitials: DEFAULT.INITIALS,
        };
        const newService = ChatBlkServiceFactory.initServices(
            defaultAppContext,
            true
        );
        console.log(
            `[${CHAT_BLK}] Unsupported app context mode, defaulting to hardcoded values`,
            newService.baseInstance.appContext
        );
    };

    return { isEmbedded, serviceInstance };
};

export default useInitServices;
